import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronRight, FileText } from 'lucide-react';
import AgentAvatar from './AgentAvatar';
import UsageLine from './UsageLine';
import { useTranslation } from '../../hooks/useTranslation.jsx';

/**
 * A thread of agent work folded under the chat (specs/kuutio-agents/README.md v1):
 * the turns of a bounce — or of any later multi-agent job — live here, verifiable,
 * expanded while running and collapsed once the report exists. The footer shows the
 * structured result, what it cost, and opens the report beside the chat.
 *
 * Props: turns (the kuutio_agent messages of this thread, in order), thread (the
 * saved record with result/report/usage, or null while running), renderTurn(message),
 * onOpenReport(thread).
 */
const Box = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  padding: 8px 10px;
  margin: 2px 0;
`;

const Head = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: #fff;
  text-align: left;
  cursor: pointer;
`;

const Avatars = styled.span`
  display: inline-flex;
  & > * + * { margin-left: -8px; }
`;

const Title = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Sub = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 400;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed rgba(255, 255, 255, 0.14);
`;

const Foot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const Chip = styled.span`
  font-size: 10.5px;
  padding: 2px 7px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.75);
`;

const OpenBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  padding: 5px 10px;
  border-radius: 8px;
  border: 1px solid rgba(76, 175, 80, 0.6);
  background: rgba(76, 175, 80, 0.18);
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  &:hover { background: rgba(76, 175, 80, 0.3); }
`;

const Running = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
`;

export default function AgentThread({ turns, thread, renderTurn, onOpenReport }) {
  const { t } = useTranslation();
  const info = turns[0]?.speaker?.thread || {};
  const done = !!thread;
  const [open, setOpen] = useState(!done);
  // Fold once the report arrives; the person can reopen it
  useEffect(() => { if (done) setOpen(false); }, [done]);

  const participants = [];
  for (const m of turns) if (!participants.some((p) => p.slug === m.speaker.slug)) participants.push(m.speaker);
  const kindLabel = t(`chatAgent.thread.kind.${info.kind || 'bounce'}`);
  const result = thread?.result;

  return (
    <Box>
      <Head type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Avatars>{participants.map((p) => <AgentAvatar key={p.slug} speaker={p} size={22} />)}</Avatars>
        <Title>
          {kindLabel}: {info.question || thread?.question || ''}
          <Sub>
            {t('chatAgent.thread.turns', { n: turns.length })}
            {info.rounds ? ` · ${t('chatAgent.thread.rounds', { n: info.rounds })}` : ''}
            {!done && info.total ? ` · ${t('chatAgent.thread.running', { done: turns.length, total: info.total })}` : ''}
          </Sub>
        </Title>
      </Head>
      {open && <Body>{turns.map(renderTurn)}</Body>}
      <Foot>
        {!done && <Running>{t('chatAgent.thread.writing')}</Running>}
        {result && result.findings && (
          <>
            <Chip>{t('chatAgent.thread.findings', { n: result.findings.length })}</Chip>
            <Chip>{t('chatAgent.thread.tensions', { n: result.tensions?.length || 0 })}</Chip>
            <Chip>{t('chatAgent.thread.gaps', { n: result.gaps?.length || 0 })}</Chip>
          </>
        )}
        {result && !result.findings && (
          <>
            <Chip>{t('chatAgent.thread.agreed', { n: result.agreed?.length || 0 })}</Chip>
            <Chip>{t('chatAgent.thread.disputed', { n: result.disputed?.length || 0 })}</Chip>
            <Chip>{t('chatAgent.thread.open', { n: result.open?.length || 0 })}</Chip>
            {result.converged && <Chip>{t('chatAgent.thread.converged')}</Chip>}
          </>
        )}
        {thread?.usage && <UsageLine usage={thread.usage} />}
        {thread?.report && onOpenReport && (
          <OpenBtn type="button" onClick={() => onOpenReport(thread)}>
            <FileText size={13} /> {t('chatAgent.thread.openReport')}
          </OpenBtn>
        )}
        {done && !thread.report && <Chip>{t('chatAgent.thread.noReport')}</Chip>}
      </Foot>
    </Box>
  );
}
